import { useMutation, useQuery, useQueryClient } from 'react-query'
import type { Document, DocumentStagingStatus, DocumentType, File } from '~/types/document.types'
import httpClient from '~/utils/httpClient'

export type useDocumentListParams = {
  documentIds?: number | number[]
  departmentIds?: number | number[]
  picUserIds?: number | number[]
  onlyMyDocuments?: boolean
  onlyMyDepartments?: boolean
  docTypes?: number | number[]
  search?: string
  stagingStatuses?: number | number[]
  liveStatuses?: number | number[]
  page?: number
  pageSize?: number
  getAll?: boolean
  includeControls?: boolean
  pendingOfficiation?: boolean
}

export const useDocumentList = (params: useDocumentListParams) => {
  return useQuery({
    queryKey: ['documents', params],
    queryFn: async () => {
      const response = await httpClient.get<{
        data: Document[]
        count: number
      }>('/api/documents', { params })
      return response?.data
    },
  })
}

export const useDocumentDetail = (id: number) => {
  return useQuery({
    queryKey: ['document', id],
    queryFn: async () => {
      const response = await httpClient.get<{ data: Document }>(`/api/documents/${id}`)
      return response?.data?.data
    },
    enabled: !!id,
  })
}

export const useDocumentFilesList = (id: number, enabled = true) => {
  return useQuery({
    queryKey: ['document', id, 'files'],
    queryFn: async () => {
      const response = await httpClient.get<{ data: File[] }>(`/api/documents/${id}/files`)
      return response?.data?.data
    },
    enabled: !!id && enabled,
  })
}

export type CreateDocumentPayload = {
  title: string
  titleId?: string
  docNumberTemplate?: string
  docType: DocumentType
  departmentId: number
  picUserId: number
  controlId?: number
}

export const useCreateDocument = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreateDocumentPayload) => {
      const response = await httpClient.post<{ data: Document }>('/api/documents', payload)
      return response?.data?.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['documents'])
    },
  })
}

export const useUpdateDocNoTemplate = (id: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (docNumberTemplate: string) => {
      const response = await httpClient.patch<{ data: Document }>(
        `/api/documents/${id}/doc_number_template`,
        {
          docNumberTemplate,
        },
      )
      return response?.data?.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['document', id])
      queryClient.invalidateQueries(['documents'])
    },
  })
}

export const useUpdateDocTitle = (id: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (req: { title?: string; titleId: string }) => {
      const response = await httpClient.patch<{ data: Document }>(
        `/api/documents/${id}/doc_title`,
        req,
      )
      return response?.data?.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['document', id])
      queryClient.invalidateQueries(['documents'])
    },
  })
}

export const useUploadDocumentFile = (id: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (file: Blob) => {
      const formData = new FormData()
      formData.append('file', file)
      const response = await httpClient.post<{ data: File }>(`/api/documents/${id}/files`, formData)
      return response?.data?.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['document', id])
      queryClient.invalidateQueries(['documents'])
    },
  })
}

export const useOfficiateDocument = (id: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ file, nextLiveVersion }: { file?: Blob; nextLiveVersion?: number }) => {
      let body: FormData | undefined

      if (file) {
        const formData = new FormData()
        formData.append('file', file)
        body = formData
      }

      const response = await httpClient.post<{ data: Document }>(
        `/api/documents/${id}/officiate`,
        body,
        {
          params: { nextLiveVersion },
        },
      )
      return response?.data?.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['document', id])
      queryClient.invalidateQueries(['documents'])
    },
  })
}

export const useReassignPic = (id: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (picUserId: number) => {
      const response = await httpClient.patch<{ data: Document }>(`/api/documents/${id}/pic`, {
        picUserId,
      })
      return response?.data?.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['document', id])
      queryClient.invalidateQueries(['documents'])
    },
  })
}

export const useUpdateStagingStatus = (id: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (stagingStatus: DocumentStagingStatus) => {
      const response = await httpClient.patch<{ data: Document }>(
        `/api/documents/${id}/staging_status`,
        {
          stagingStatus,
        },
      )
      return response?.data?.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['document', id])
      queryClient.invalidateQueries(['documents'])
    },
  })
}

export const useUpdateDepartment = (id: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (departmentId: number) => {
      const response = await httpClient.patch<{ data: Document }>(
        `/api/documents/${id}/department`,
        {
          departmentId,
        },
      )
      return response?.data?.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['document', id])
      queryClient.invalidateQueries(['documents'])
    },
  })
}

export const useArchiveDocument = (id: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const response = await httpClient.post<{ data: Document }>(`/api/documents/${id}/archive`)
      return response?.data?.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['document', id])
      queryClient.invalidateQueries(['documents'])
    },
  })
}
